import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, Stack, Typography, Grid, Button, TextField, MenuItem, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Alert, Tooltip, Chip, Divider, InputAdornment, Autocomplete, Avatar,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LibraryBooks as SubjectIcon, Add as AddIcon, Edit as EditIcon,
  Delete as DeleteIcon, Search as SearchIcon, DeleteSweep as DeleteSetIcon,
  Refresh as RefreshIcon, HistoryEdu as HistoryIcon,
  Class as ClassIcon, RemoveCircleOutline as RemoveRowIcon,
  PlaylistAdd as MoreRowsIcon, FilterAltOff as ClearFiltersIcon,
  Close as CloseIcon, Description as RawSheetIcon,
  WarningAmber as WarningIcon, ContentCopy as DupInFormIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import RawScoreSheetDialog from './RawScoreSheetDialog';

// Canonical term codes — the rest of the system (receipts, scores,
// payments, students, reports) all use Term_1 / Term_2 / Term_3. The
// settings TermDialog enforces the same set so search joins line up
// cleanly across modules.
const TERM_CHOICES = ['Term_1', 'Term_2', 'Term_3'];

// editBlank mirrors the row shape used by the table: `sub` = subject
// name, `sub_t` = teacher (legacy column quirk — that's where the
// teacher actually lives in `ex_re`).
const editBlank = {
  cla: '', term: '', sub: '', sub_t: '', status: 'Core Subject',
  state: 'Term Exam', acc_yer: '',
};

const bulkBlank = () => ({
  cla: '', state: 'Term Exam',
  // Which sitting of a repeated exam (Mock 1, Mock 2 …). 'new' means
  // "start the next one" and is what a fresh dialog defaults to; the
  // server assigns the number. Ignored for a once-a-term exam.
  exam_num: 'new',
  // `applyTeacher` is the "one teacher for all subjects" picker on the
  // dialog header — it's copied into every empty row's teacher field
  // when submitting. Per-row teachers always override it.
  applyTeacher: '',
  // The staff account behind `applyTeacher`, when one was picked from
  // the list rather than typed. Null for free text.
  applyTeacherId: null,
  // Each row carries a stable id so React.memo can skip re-rendering
  // rows whose data hasn't changed.
  subjects: [emptyRow(), emptyRow(), emptyRow()],
});
// The dialog row carries: subject name (sub), teacher name (sub_t —
// legacy column name, but that's where the teacher actually lives),
// category (status). `class_t` is intentionally NOT in the model — in
// the underlying `ex_re` table it's just a duplicate of the class.
// Each row carries a stable `id` so React can skip re-renders for
// rows whose data didn't change.
let __rowSeq = 0;
const nextRowId = () => `r${++__rowSeq}`;
// `sub_t` is the teacher's NAME (what ex_re stores); `sub_t_id` is the
// staff account behind it. The id is what lets registering a subject
// also assign it — a name on its own links to no login and so reaches
// nobody's portal.
const emptyRow = () => ({ id: nextRowId(), sub: '', sub_t: '', sub_t_id: null, status: 'Core Subject' });

// The picker's options are staff objects, but a freeSolo Autocomplete
// also hands back plain strings when someone types. One reader for both.
const teacherLabel = (o) =>
  (typeof o === 'string' ? o : (o?.name || '')) || '';

// Stable empty array reference — avoids the `prop || []` pattern that
// allocates a fresh array on every render and breaks memo equality.
const EMPTY_LIST = Object.freeze([]);

// Shared "professional table" sx — one consistent look for both the
// History and Subjects tables. Hairline borders, uppercase 10.5px
// headers with letter spacing, zebra rows that wash out on hover,
// tabular numerics in number cells, sticky-header friendly.
const PRO_TABLE = {
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 800,
    fontSize: 10.5,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: 'text.secondary',
    bgcolor: '#f7f9fc',
    py: 1,
    borderBottom: '1px solid #d9dee6',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    transition: 'background-color 120ms ease',
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even):not(.row-active)':
    { backgroundColor: '#fafbfc' },
  '& .MuiTableBody-root .MuiTableRow-root:hover':
    { backgroundColor: 'rgba(25,118,210,0.06) !important' },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: 12.5,
    py: 0.9,
    borderBottom: '1px solid #eef0f4',
    fontVariantNumeric: 'tabular-nums',
  },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
    borderBottom: 0,
  },
};

export default function RegisterSubjectPage() {
  const [rows,    setRows]    = useState([]);
  const [groups,  setGroups]  = useState([]);
  const [catalog, setCatalog] = useState([]);
  // Active academic period (term + year) the administrator set in
  // Examination Settings. Every new subject registration is forced
  // into this bucket — the dialog no longer asks the user.
  const [activePeriod, setActivePeriod] = useState(null);
  const [lookups, setLookups] = useState({
    classes: [], terms: [], acc_years: [], teachers: [],
    exam_states: ['Term Exam', 'Mock', 'Mid-Term Test'],
    // Exam types that can be sat more than once in the same term. The
    // server sends the real list; this is only the fallback for an older
    // backend that does not yet.
    repeatable_exam_states: ['Mock', 'Mid-Term Test'],
    subject_statuses: ['Core Subject', 'Elective Subject', 'Mandatory', 'Optional Subject'],
  });

  // Filters drive both the right-hand subjects list and the highlight
  // on the left-hand history list.
  const [fCla, setFCla] = useState('');
  const [fTerm, setFTerm] = useState('');
  const [fState, setFState] = useState('');
  const [fYer, setFYer] = useState('');
  const [fQ, setFQ] = useState('');
  // Which SITTING of a repeated exam. '' means every sitting.
  const [fNum, setFNum] = useState('');

  // A terminal exam happens once a term. A mock does not — a class may
  // sit five or six in one term — so those carry a sitting number and
  // the screen has to let you say which one you mean.
  const isRepeatable = (state) =>
    (lookups.repeatable_exam_states || []).some(
      s => String(s).toLowerCase() === String(state || '').trim().toLowerCase());

  // "Mock 2", but just "Term Exam" for the once-a-term ones.
  const sittingLabel = (state, num) =>
    isRepeatable(state) ? `${state} ${Number(num) || 1}` : (state || '—');

  // Bulk add dialog
  const [bulkOpen, setBulkOpen]   = useState(false);
  const [bulkForm, setBulkForm]   = useState(bulkBlank());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkErr, setBulkErr]     = useState('');

  // Single edit dialog (kept for "Edit" pencil from the table)
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [editForm, setEditForm] = useState(editBlank);
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr]   = useState('');

  // Raw Score Sheet dialog — opened from the History row's sheet icon.
  // `rawSheetBucket` carries the class/term/state/year of the row the
  // user clicked; null when the dialog is closed.
  const [rawSheetBucket, setRawSheetBucket] = useState(null);

  // Separate loading states so each panel can show its own spinner.
  // listLoading is for the right-hand subjects table; groupsLoading is
  // for the left-hand history table. Refresh hits both at once.
  const [listLoading, setListLoading]     = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const loadLookups = async () => {
    try { setLookups((await API.get('/examination/lookups')).data); }
    catch { /* leave defaults */ }
  };
  // Groups load now scopes to the active filters (term + year) so the
  // History panel only fetches what the user can actually see. Callers
  // pass in overrides when the filter state hasn't propagated yet
  // (e.g. right after priming from the active period on first load).
  const loadGroups = async (override = {}) => {
    setGroupsLoading(true);
    try {
      const params = {};
      const term = 'term' in override ? override.term : fTerm;
      const yer  = 'acc_yer' in override ? override.acc_yer : fYer;
      if (term) params.term    = term;
      if (yer)  params.acc_yer = yer;
      setGroups((await API.get('/examination/subjects/groups', { params })).data || []);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load subject groups')); }
    finally { setGroupsLoading(false); }
  };
  const loadCatalog = async () => {
    try { setCatalog((await API.get('/examination/subjects/catalog')).data || []); }
    catch { /* graceful — autocomplete still works with free text */ }
  };
  const loadActivePeriod = async () => {
    try {
      const { data } = await API.get('/examination/settings');
      setActivePeriod(data?.period || null);
    } catch { setActivePeriod(null); }
  };
  // loadList also accepts overrides for the same reason as loadGroups —
  // the initial-load chain hands the period-derived filters in directly
  // before they've made it into state.
  const loadList = async (override = {}) => {
    setListLoading(true);
    try {
      const params = {};
      const cla   = 'cla'     in override ? override.cla     : fCla;
      const term  = 'term'    in override ? override.term    : fTerm;
      const state = 'state'   in override ? override.state   : fState;
      const yer   = 'acc_yer' in override ? override.acc_yer : fYer;
      const q     = 'q'       in override ? override.q       : fQ;
      const num   = 'exam_num' in override ? override.exam_num : fNum;
      if (cla)   params.cla     = cla;
      if (term)  params.term    = term;
      if (state) params.state   = state;
      if (yer)   params.acc_yer = yer;
      if (q)     params.q       = q;
      // Only meaningful on a repeated exam; sent only when chosen.
      if (num)   params.exam_num = num;
      setRows((await API.get('/examination/subjects', { params })).data || []);
    } catch (e) { swal.error(swal.pickError(e, 'Load failed')); }
    finally { setListLoading(false); }
  };

  // Initial-load chain — fetch the active period FIRST, prime the
  // filter state from it, then scope every subsequent load to that
  // period. Avoids the previous behaviour of dragging every historical
  // group + every historical subject into memory on first paint.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      loadLookups();
      loadCatalog();
      let period = null;
      try {
        const { data } = await API.get('/examination/settings');
        period = data?.period || null;
        if (cancelled) return;
        setActivePeriod(period);
      } catch { /* leave activePeriod null */ }
      const term = period?.term || '';
      const yer  = period?.acc  || '';
      if (term) setFTerm(term);
      if (yer)  setFYer(yer);
      // Kick the first loads with the period filters baked in — these
      // run in parallel and are the only data the user sees until they
      // change a filter.
      loadGroups({ term, acc_yer: yer });
      loadList({ term, acc_yer: yer });
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, []);

  // Subsequent filter changes — refetch the right panel. The History
  // panel also refetches when term/year change so it stays scoped to
  // whatever period the user is currently viewing.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    loadList();
    /* eslint-disable-next-line */
  }, [fCla, fTerm, fState, fYer, fNum]);

  // A sitting only means something inside one exam type. Switching from
  // Mock to Term Exam must not leave "2" filtering a list that has no
  // sitting 2 — that would look like the subjects had vanished.
  useEffect(() => {
    if (!isRepeatable(fState)) setFNum('');
    /* eslint-disable-next-line */
  }, [fState]);
  useEffect(() => {
    if (firstRender.current) return;
    loadGroups();
    /* eslint-disable-next-line */
  }, [fTerm, fYer]);

  const openBulkAdd = () => {
    setBulkForm({
      ...bulkBlank(),
      // Pre-fill with whatever class / exam type the user is currently
      // looking at so the new set lands inside the same bucket. Term and
      // year are read from the active period at submit time.
      cla:   fCla   || '',
      state: fState || 'Term Exam',
      // If the user is looking at one sitting, offer to add to it;
      // otherwise start a new one.
      exam_num: fNum && isRepeatable(fState) ? String(fNum) : 'new',
    });
    setBulkErr(''); setBulkOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setEditForm({
      cla: row.cla, term: row.term,
      sub:   row.sub   || '',
      sub_t: row.sub_t || '',       // teacher
      status: row.status,
      state:  row.state,
      acc_yer: row.acc_yer || '',
    });
    setEditErr(''); setEditOpen(true);
  };

  const saveBulk = async () => {
    setBulkSaving(true); setBulkErr('');
    try {
      const payload = {
        cla:     bulkForm.cla,
        state:   bulkForm.state,
        // 'new' asks the server for the next sitting; a number adds to
        // that existing one. Harmless on a once-a-term exam, which the
        // server pins to sitting 1 regardless.
        exam_num: bulkForm.exam_num ?? 'new',
        // Top-level `teacher` is the "Apply to all" picker — the backend
        // uses it as the fallback when a row has no teacher of its own.
        // Term + year are not sent; the backend snaps them to the
        // active academic period.
        teacher: bulkForm.applyTeacher || '',
        // The staff account behind each name. This is what turns the
        // choice into a teacher_assignment, so the subject lands on that
        // teacher's portal without a second trip to another screen.
        teacher_user_id: bulkForm.applyTeacherId || null,
        subjects: bulkForm.subjects
          .filter(r => r.sub && r.sub.trim())
          .map(r => ({
            sub:     r.sub.trim(),
            status:  r.status || 'Core Subject',
            teacher: (r.sub_t || '').trim(),
            teacher_user_id: r.sub_t_id || null,
          })),
      };
      if (!payload.cla) {
        setBulkErr('Pick a class'); setBulkSaving(false); return;
      }
      if (payload.subjects.length === 0) {
        setBulkErr('Add at least one subject'); setBulkSaving(false); return;
      }
      if (!activePeriod?.term || !activePeriod?.acc) {
        setBulkErr('No active academic period set. Open Examination Settings and choose the current term/year first.');
        setBulkSaving(false); return;
      }
      const { data } = await API.post('/examination/subjects/bulk', payload);
      setBulkOpen(false);
      await Promise.all([loadList(), loadGroups(), loadCatalog()]);
      swal.success(data?.message || 'Subjects registered');
    } catch (e) { setBulkErr(e.response?.data?.message || 'Save failed'); }
    finally { setBulkSaving(false); }
  };

  const saveEdit = async () => {
    setEditSaving(true); setEditErr('');
    try {
      await API.put(`/examination/subjects/${editing.tr}`, editForm);
      setEditOpen(false);
      await Promise.all([loadList(), loadGroups(), loadCatalog()]);
      swal.success('Subject updated');
    } catch (e) { setEditErr(e.response?.data?.message || 'Save failed'); }
    finally { setEditSaving(false); }
  };

  const remove = async (row) => {
    const ok = await swal.confirm({
      title: 'Remove subject',
      text: `Remove "${row.sub}" from ${row.cla} (${row.term}, ${row.state})?`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await API.delete(`/examination/subjects/${row.tr}`);
      await Promise.all([loadList(), loadGroups()]);
    } catch (e) { swal.error(swal.pickError(e, 'Remove failed')); }
  };

  const removeSet = async (g) => {
    // Name the sitting in the prompt. "Delete all 10 subjects for
    // J.H.S. 3 - Mock" reads as though it would take every mock the
    // class has sat; "Mock 2" says exactly what goes.
    const label = sittingLabel(g.state, g.exam_num);
    const ok = await swal.confirm({
      title: 'Remove entire subject set',
      text: `Delete all ${g.n_subjects} subject(s) registered for ${g.cla} - ${g.term} - ${label} (${g.acc_yer || 'no year'})?`,
      tone: 'danger',
      confirmText: 'Yes, delete set',
    });
    if (!ok) return;
    try {
      await API.delete('/examination/subjects/set', {
        data: {
          cla: g.cla, term: g.term, state: g.state, acc_yer: g.acc_yer || '',
          // Only this sitting. Without it the server would clear every
          // sitting of the exam type.
          exam_num: g.exam_num ?? '',
        },
      });
      await Promise.all([loadList(), loadGroups()]);
      swal.success('Subject set removed');
    } catch (e) { swal.error(swal.pickError(e, 'Remove failed')); }
  };

  const applyGroupAsFilter = (g) => {
    setFCla(g.cla || ''); setFTerm(g.term || '');
    setFState(g.state || ''); setFYer(g.acc_yer || '');
    setFNum(isRepeatable(g.state) && g.exam_num ? String(g.exam_num) : '');
  };

  const clearAllFilters = () => {
    setFCla(''); setFTerm(''); setFState(''); setFYer(''); setFQ(''); setFNum('');
    setHistoryQ('');
  };

  const statusChipColor = (status) => {
    if (status === 'Core Subject')     return 'primary';
    if (status === 'Elective Subject') return 'secondary';
    if (status === 'Mandatory')        return 'success';
    if (status === 'Optional Subject') return 'warning';
    return 'default';
  };

  // Highlight the active group in the left history panel. The sitting is
  // part of the key, so clicking Mock 2 highlights Mock 2 and not Mock 1.
  const groupKey = (g) =>
    `${g.cla}|${g.term}|${g.state}|${g.acc_yer || ''}|${isRepeatable(g.state) ? (g.exam_num ?? 1) : ''}`;
  const activeKey = `${fCla}|${fTerm}|${fState}|${fYer || ''}|${isRepeatable(fState) ? (fNum || '') : ''}`;
  const anyFilter = fCla || fTerm || fState || fYer || fNum;

  const filterChips = useMemo(() => {
    const chips = [];
    if (fCla)   chips.push({ label: `Class: ${fCla}`,   onDelete: () => setFCla('') });
    if (fTerm)  chips.push({ label: `Term: ${fTerm}`,   onDelete: () => setFTerm('') });
    if (fState) chips.push({ label: `Exam: ${fState}`,  onDelete: () => setFState('') });
    if (fNum && isRepeatable(fState)) {
      chips.push({ label: `Sitting: ${fState} ${fNum}`, onDelete: () => setFNum('') });
    }
    if (fYer)   chips.push({ label: `Year: ${fYer}`,    onDelete: () => setFYer('') });
    if (fQ)     chips.push({ label: `Search: ${fQ}`,    onDelete: () => setFQ('') });
    return chips;
    /* eslint-disable-next-line */
  }, [fCla, fTerm, fState, fYer, fQ, fNum]);

  // ── Navigating the History list ─────────────────────────────────
  //
  // A term's History is a flat run of sets — fourteen of them in a small
  // school, far more in a big one — and the one mock you actually want
  // sits somewhere in the middle looking exactly like the thirteen term
  // exams around it. Finding it meant reading the list.
  //
  // Everything below narrows the list IN MEMORY. `groups` is already
  // loaded for the chosen term and year, so switching between Term Exam,
  // Mock and Mid-Term is instant — no request, no spinner.

  // Free-text narrowing of the History list by class name.
  const [historyQ, setHistoryQ] = useState('');

  // How many sets each exam type holds, for the counts on the tabs. Shown
  // so an empty tab is obvious BEFORE clicking it.
  const examTabCounts = useMemo(() => {
    const m = new Map();
    for (const g of groups) {
      const k = g.state || '—';
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [groups]);

  // The exam types worth showing a tab for: the standard ones, plus
  // anything historical that is actually present in the data.
  const examTabs = useMemo(() => {
    const seen = new Set(lookups.exam_states || EMPTY_LIST);
    for (const g of groups) if (g.state) seen.add(g.state);
    return [...seen];
  }, [lookups.exam_states, groups]);

  // What the History table actually draws.
  const visibleGroups = useMemo(() => {
    const q = historyQ.trim().toLowerCase();
    return groups.filter(g => {
      if (fState && g.state !== fState) return false;
      if (fNum && isRepeatable(g.state)
          && String(g.exam_num ?? 1) !== String(fNum)) return false;
      if (q && !String(g.cla || '').toLowerCase().includes(q)) return false;
      return true;
    });
    /* eslint-disable-next-line */
  }, [groups, fState, fNum, historyQ]);

  // Which sittings exist for what is currently filtered. Read off the
  // History groups already in memory rather than asking the server —
  // History is loaded for the same term and year, so it already knows.
  const sittingChoices = useMemo(() => {
    const seen = new Set();
    for (const g of groups) {
      if (fCla && g.cla !== fCla) continue;
      if (fState && g.state !== fState) continue;
      if (fTerm && g.term !== fTerm) continue;
      if (fYer && (g.acc_yer || '') !== fYer) continue;
      seen.add(Number(g.exam_num) || 1);
    }
    return [...seen].sort((a, b) => a - b);
  }, [groups, fCla, fState, fTerm, fYer]);

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      {/* Compact banner — page title + active academic period + stats
          + "Add subjects" button all on one row. Saves the vertical
          space the old ModuleHeader + PeriodHero stack was eating. */}
      <PeriodHero activePeriod={activePeriod}
        groupCount={groups.length} subjectCount={rows.length}
        onAdd={openBulkAdd} />

      {/* Filter card — modern, sectioned, with subtle styling */}
      <Card sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: 1, borderColor: 'divider',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}>
          <SearchIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1 }}>
            Filters
          </Typography>
          {anyFilter && (
            <Button size="small" startIcon={<ClearFiltersIcon />} onClick={clearAllFilters}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Clear
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => { loadGroups(); loadCatalog(); loadList(); loadActivePeriod(); }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ p: 1.6 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}
            alignItems={{ md: 'center' }} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <TextField select size="small" label="Class" value={fCla}
              onChange={e => setFCla(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value=""><em>All classes</em></MenuItem>
              {lookups.classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Term" value={fTerm}
              onChange={e => setFTerm(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value=""><em>All terms</em></MenuItem>
              {TERM_CHOICES.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Exam Type" value={fState}
              onChange={e => setFState(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value=""><em>All</em></MenuItem>
              {lookups.exam_states.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            {/* Only for exams that repeat. A terminal exam has one
                sitting, so the picker would be a dropdown with a single
                choice. */}
            {isRepeatable(fState) && (
              <TextField select size="small" label="Sitting" value={fNum}
                onChange={e => setFNum(e.target.value)} sx={{ minWidth: 130 }}>
                <MenuItem value=""><em>All sittings</em></MenuItem>
                {sittingChoices.map(n => (
                  <MenuItem key={n} value={String(n)}>{`${fState} ${n}`}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField select size="small" label="Year" value={fYer}
              onChange={e => setFYer(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value=""><em>All</em></MenuItem>
              {lookups.acc_years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            <TextField size="small" placeholder="Search subject or teacher" value={fQ}
              onChange={e => setFQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadList()}
              sx={{ flex: 1, minWidth: 220 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Stack>
          {filterChips.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.2, flexWrap: 'wrap', gap: 0.5 }}>
              {filterChips.map((c, i) => (
                <Chip key={i} size="small" label={c.label} onDelete={c.onDelete}
                  sx={{ fontWeight: 700 }} />
              ))}
            </Stack>
          )}
        </Box>
      </Card>

      {/* Side-by-side: History (left) · Class subjects (right) */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%', borderRadius: 2, overflow: 'hidden',
            border: 1, borderColor: 'divider' }}>
            <PanelHeader icon={<HistoryIcon />} title="History"
              subtitle={visibleGroups.length === groups.length
                ? `${groups.length} set${groups.length === 1 ? '' : 's'} on record`
                : `${visibleGroups.length} of ${groups.length} sets shown`}
              color="info"
              right={groupsLoading ? <CircularProgress size={16} thickness={5} /> : null} />

            {/* ── Jump straight to one kind of exam ──────────────
                One tap each, filtered in memory, so moving between
                Term Exam and Mock is instant. The counts are on the
                tabs so an empty one is obvious before it is clicked. */}
            <Box sx={{ px: 1.5, pt: 1.2, pb: 1, borderBottom: 1, borderColor: 'divider',
              bgcolor: (t) => alpha(t.palette.info.main, 0.03) }}>
              <ToggleButtonGroup exclusive size="small" value={fState || 'ALL'}
                onChange={(_, v) => { if (v !== null) setFState(v === 'ALL' ? '' : v); }}
                sx={{ flexWrap: 'wrap', gap: 0.5,
                  '& .MuiToggleButton-root': {
                    textTransform: 'none', fontWeight: 800, fontSize: 12,
                    px: 1.2, py: 0.35, border: '1px solid', borderColor: 'divider',
                    borderRadius: '16px !important',
                  } }}>
                <ToggleButton value="ALL">
                  All
                  <Chip size="small" label={groups.length}
                    sx={{ ml: 0.6, height: 17, fontSize: 10.5, fontWeight: 800 }} />
                </ToggleButton>
                {examTabs.map(s => (
                  <ToggleButton key={s} value={s}
                    disabled={!examTabCounts.get(s)}>
                    {s}
                    <Chip size="small" label={examTabCounts.get(s) || 0}
                      color={examTabCounts.get(s) ? 'primary' : 'default'}
                      sx={{ ml: 0.6, height: 17, fontSize: 10.5, fontWeight: 800 }} />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {/* Sittings of the chosen exam — Mock 1, Mock 2, Mock 3.
                  Only for the types that repeat, and only once more than
                  one exists, so a single mock does not grow a row of
                  buttons with nothing to choose between. */}
              {isRepeatable(fState) && sittingChoices.length > 1 && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.8, flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label="All sittings"
                    color={!fNum ? 'secondary' : 'default'}
                    variant={!fNum ? 'filled' : 'outlined'}
                    onClick={() => setFNum('')}
                    sx={{ fontWeight: 800, height: 22 }} />
                  {sittingChoices.map(n => (
                    <Chip key={n} size="small" label={`${fState} ${n}`}
                      color={String(fNum) === String(n) ? 'secondary' : 'default'}
                      variant={String(fNum) === String(n) ? 'filled' : 'outlined'}
                      onClick={() => setFNum(String(n))}
                      sx={{ fontWeight: 800, height: 22 }} />
                  ))}
                </Stack>
              )}

              {/* Narrow a long list to one class without leaving the
                  panel — typing "jhs" is faster than opening the Class
                  dropdown and reading it. */}
              <TextField size="small" fullWidth placeholder="Find a class…"
                value={historyQ} onChange={e => setHistoryQ(e.target.value)}
                sx={{ mt: 0.9,
                  '& .MuiInputBase-root': { fontSize: 12.5, bgcolor: 'background.paper' },
                  '& .MuiInputBase-input': { py: 0.55 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16 }} />
                    </InputAdornment>
                  ),
                  endAdornment: historyQ ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setHistoryQ('')} sx={{ p: 0.2 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }} />
            </Box>

            <TableContainer sx={{ maxHeight: 620 }}>
              <Table size="small" stickyHeader sx={PRO_TABLE}>
                <TableHead>
                  <TableRow>
                    <TableCell>Class</TableCell>
                    <TableCell>Term</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Exam</TableCell>
                    <TableCell align="right">Subjects</TableCell>
                    <TableCell align="right" sx={{ width: 84 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupsLoading && groups.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={20} />
                    </TableCell></TableRow>
                  )}
                  {!groupsLoading && groups.length === 0 && (
                    <TableRow><TableCell colSpan={6}>
                      <EmptyState icon={<HistoryIcon sx={{ fontSize: 32 }} />}
                        title="No subject sets yet"
                        subtitle="Click 'Add subjects' above to register the first set." />
                    </TableCell></TableRow>
                  )}
                  {/* Sets exist, but none match the tab / class typed —
                      say which, rather than showing a bare empty table. */}
                  {!groupsLoading && groups.length > 0 && visibleGroups.length === 0 && (
                    <TableRow><TableCell colSpan={6}>
                      <EmptyState icon={<HistoryIcon sx={{ fontSize: 32 }} />}
                        title={fState ? `No ${fState} registered here` : 'Nothing matches'}
                        subtitle={historyQ
                          ? `No class matching "${historyQ}" in this term.`
                          : 'Try another exam type, or a different term and year above.'} />
                    </TableCell></TableRow>
                  )}
                  {visibleGroups.map((g) => {
                    const isActive = activeKey === groupKey(g);
                    return (
                      <TableRow key={groupKey(g)} hover
                        className={isActive ? 'row-active' : ''}
                        onClick={() => applyGroupAsFilter(g)}
                        sx={{
                          cursor: 'pointer',
                          ...(isActive && {
                            bgcolor: (t) => alpha(t.palette.primary.main, 0.10) + ' !important',
                            borderLeft: '3px solid',
                            borderColor: 'primary.main',
                          }),
                        }}>
                        <TableCell sx={{ fontWeight: 800, color: 'text.primary' }}>
                          {g.cla || '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {(g.term || '—').replace('_', ' ')}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {g.acc_yer || '—'}
                        </TableCell>
                        <TableCell>
                          {/* "Mock 2", not just "Mock" — six mocks used to
                              collapse into one indistinguishable row. */}
                          <Chip size="small" label={sittingLabel(g.state, g.exam_num)}
                            variant="outlined"
                            color={isRepeatable(g.state) ? 'secondary' : 'default'}
                            sx={{ fontWeight: 700, height: 20, fontSize: 11,
                              ...(isRepeatable(g.state) ? {} : { borderColor: 'divider' }) }} />
                        </TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={g.n_subjects} color="primary"
                            sx={{ fontWeight: 800, height: 22, minWidth: 32, fontSize: 11.5 }} />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Raw score sheet (PDF / Excel)">
                            <IconButton size="small" color="primary"
                              onClick={(e) => { e.stopPropagation(); setRawSheetBucket(g); }}
                              sx={{ p: 0.5 }}>
                              <RawSheetIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete this set">
                            <IconButton size="small" color="error"
                              onClick={(e) => { e.stopPropagation(); removeSet(g); }}
                              sx={{ p: 0.5 }}>
                              <DeleteSetIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%', borderRadius: 2, overflow: 'hidden',
            border: 1, borderColor: 'divider' }}>
            <PanelHeader icon={<ClassIcon />}
              title={anyFilter
                ? `${fCla || 'All classes'}${fTerm ? ` · ${fTerm.replace('_', ' ')}` : ''}${fState ? ` · ${fState}${fNum && isRepeatable(fState) ? ` ${fNum}` : ''}` : ''}${fYer ? ` · ${fYer}` : ''}`
                : 'All registered subjects'}
              subtitle={`${rows.length} subject${rows.length === 1 ? '' : 's'} shown`}
              color="primary"
              right={listLoading ? <CircularProgress size={16} thickness={5} /> : null} />
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table size="small" stickyHeader sx={PRO_TABLE}>
                <TableHead>
                  <TableRow>
                    <TableCell>Subject</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Term</TableCell>
                    <TableCell>Exam</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Teacher</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listLoading && (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={20} />
                    </TableCell></TableRow>
                  )}
                  {!listLoading && rows.length === 0 && (
                    <TableRow><TableCell colSpan={7}>
                      <EmptyState icon={<SubjectIcon sx={{ fontSize: 32 }} />}
                        title="No subjects match the current filters"
                        subtitle="Adjust the filters above, or register a new set." />
                    </TableCell></TableRow>
                  )}
                  {!listLoading && rows.map(r => (
                    <TableRow key={r.tr} hover>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary' }}>
                        {r.sub}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {r.cla}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {(r.term || '').replace('_', ' ')}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={sittingLabel(r.state, r.exam_num)}
                          variant="outlined"
                          color={isRepeatable(r.state) ? 'secondary' : 'default'}
                          sx={{ fontWeight: 700, height: 20, fontSize: 11,
                            ...(isRepeatable(r.state) ? {} : { borderColor: 'divider' }) }} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={statusChipColor(r.status)} label={r.status}
                          sx={{ fontWeight: 700, height: 20, fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ color: r.sub_t ? 'text.primary' : 'text.disabled',
                        fontStyle: r.sub_t ? 'normal' : 'italic' }}>
                        {r.sub_t || 'unassigned'}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(r)} sx={{ p: 0.5 }}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => remove(r)} sx={{ p: 0.5 }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Bulk add dialog */}
      <BulkAddDialog
        open={bulkOpen} onClose={() => setBulkOpen(false)}
        form={bulkForm} setForm={setBulkForm}
        saving={bulkSaving} err={bulkErr}
        onSave={saveBulk}
        lookups={lookups} catalog={catalog}
        activePeriod={activePeriod} />

      {/* Single edit dialog */}
      <EditSingleDialog
        open={editOpen} onClose={() => setEditOpen(false)}
        form={editForm} setForm={setEditForm}
        saving={editSaving} err={editErr}
        onSave={saveEdit}
        lookups={lookups} catalog={catalog} />

      {/* Raw Score Sheet — print + Excel export driven by the active
          assessment profile for the picked class. */}
      <RawScoreSheetDialog
        open={!!rawSheetBucket}
        bucket={rawSheetBucket}
        onClose={() => setRawSheetBucket(null)} />
    </Box>
  );
}

// Panel header used by the two side-by-side cards
function PanelHeader({ icon, title, subtitle, color = 'primary', right }) {
  return (
    <Box sx={{
      px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.2,
      borderBottom: 1, borderColor: 'divider',
      background: (t) => `linear-gradient(90deg, ${alpha(t.palette[color].main, 0.10)} 0%, ${alpha(t.palette[color].main, 0.02)} 100%)`,
    }}>
      <Avatar sx={{ bgcolor: (t) => alpha(t.palette[color].main, 0.18),
        color: `${color}.main`, width: 36, height: 36 }}>
        {icon}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={800} noWrap sx={{ fontSize: 14.5 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        )}
      </Box>
      {right}
    </Box>
  );
}

// Combined banner — page title + active academic period + stats + the
// "Add subjects" CTA, all on a single row. Replaces what used to be a
// stacked ModuleHeader + hero so the filters and tables sit higher up.
function PeriodHero({ activePeriod, groupCount, subjectCount, onAdd }) {
  const ready = !!(activePeriod?.term && activePeriod?.acc);
  return (
    <Card sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{
        background: (t) => ready
          ? `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`
          : `linear-gradient(135deg, ${t.palette.warning.main} 0%, ${t.palette.error.main} 100%)`,
        color: '#fff', px: { xs: 2, md: 2.5 }, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 1.8, flexWrap: 'wrap', rowGap: 1.2,
      }}>
        {/* Left — title block */}
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 42, height: 42, flexShrink: 0 }}>
          <SubjectIcon />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1, fontSize: { xs: 17, md: 19 } }}>
            Register Subject
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 11.5 }}>
            Subjects per class & exam type — pick a history row to load it on the right
          </Typography>
        </Box>

        {/* Middle — active period strip (only when ready) */}
        <Box sx={{ flex: 1, minWidth: 0 }} />

        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          px: 1.4, py: 0.7, borderRadius: 1.5,
          bgcolor: 'rgba(255,255,255,0.14)',
          border: '1px solid rgba(255,255,255,0.22)',
        }}>
          <Typography variant="caption" sx={{
            opacity: 0.92, textTransform: 'uppercase', letterSpacing: 0.6,
            fontWeight: 800, fontSize: 9.5,
          }}>
            Active Period
          </Typography>
          {ready ? (
            <Typography fontWeight={800} sx={{ fontSize: 13.5, lineHeight: 1 }}>
              {(activePeriod.term || '').replace('_', ' ')} · {activePeriod.acc}
            </Typography>
          ) : (
            <Typography fontWeight={800} sx={{ fontSize: 12.5, lineHeight: 1 }}>
              Not set — open Examination Settings
            </Typography>
          )}
        </Box>

        <HeroStat label="Sets"     value={groupCount} />
        <HeroStat label="Subjects" value={subjectCount} />

        <Button variant="contained" color="inherit" startIcon={<AddIcon />}
          onClick={onAdd} disabled={!ready}
          sx={{
            textTransform: 'none', fontWeight: 800, color: 'primary.main',
            bgcolor: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' },
            boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
            whiteSpace: 'nowrap',
          }}>
          Add subjects
        </Button>
      </Box>
    </Card>
  );
}

function HeroStat({ label, value }) {
  return (
    <Box sx={{ px: 1.2, py: 0.5, borderRadius: 1.2, minWidth: 56,
      bgcolor: 'rgba(255,255,255,0.16)', textAlign: 'center',
      border: '1px solid rgba(255,255,255,0.18)' }}>
      <Typography sx={{ fontWeight: 900, fontSize: 16, lineHeight: 1 }}>
        {Number(value || 0)}
      </Typography>
      <Typography sx={{ opacity: 0.88, fontSize: 9,
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800 }}>
        {label}
      </Typography>
    </Box>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
      <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 1.2,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
        color: 'primary.main' }}>
        {icon}
      </Avatar>
      <Typography fontWeight={700} sx={{ color: 'text.primary' }}>{title}</Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.3 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// ── Bulk-add dialog ───────────────────────────────────────────────
// One set of common fields (class / term / year / exam / class teacher)
// + a vertical list of subject rows. Each row is an Autocomplete bound
// to the catalog of subjects the system already knows, so the user
// picks instead of retyping. "+ Add another subject" expands the list.
function BulkAddDialog({ open, onClose, form, setForm, saving, err, onSave, lookups, catalog, activePeriod }) {
  // Stable callbacks — keyed on row id, not array index. This lets
  // React.memo skip re-rendering rows whose data didn't change, even
  // when a sibling row is being typed in.
  const updateRowById = useCallback((id, patch) => {
    setForm(f => ({
      ...f,
      subjects: f.subjects.map(r => r.id === id ? { ...r, ...patch } : r),
    }));
  }, [setForm]);
  const removeRowById = useCallback((id) => {
    setForm(f => ({ ...f, subjects: f.subjects.filter(r => r.id !== id) }));
  }, [setForm]);
  const addRow = useCallback(() => {
    setForm(f => ({ ...f, subjects: [...f.subjects, emptyRow()] }));
  }, [setForm]);

  // "Apply to all" — copies the picked teacher into every row's
  // teacher field (row.sub_t) AND saves it on the form so empty rows
  // added later inherit it too. Only fires on commit (onChange), never
  // on every keystroke — that was the source of the typing lag.
  const applyTeacherToAll = useCallback((picked) => {
    const name = teacherLabel(picked);
    const uid  = picked && typeof picked === 'object' ? (picked.id || null) : null;
    setForm(f => ({
      ...f,
      applyTeacher: name,
      applyTeacherId: uid,
      subjects: f.subjects.map(r => ({ ...r, sub_t: name, sub_t_id: uid })),
    }));
  }, [setForm]);

  // Pre-compute the subject options list ONCE per catalog change so
  // each row doesn't rebuild it on every render.
  const subjectOptions = useMemo(() => {
    const set = new Set();
    for (const c of catalog) {
      const name = (c.sub || '').trim();
      if (name) set.add(name);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [catalog]);

  // Look-up of catalog entries by subject name — for the teacher hint.
  const catalogBySub = useMemo(() => {
    const m = new Map();
    for (const c of catalog) {
      const name = (c.sub || '').trim();
      if (name && !m.has(name)) m.set(name, c);
    }
    return m;
  }, [catalog]);

  // Staff who hold a login — the same list the Teacher Assignments
  // screen works from. Choosing from here is what lets the registration
  // create the assignment, which is what puts the subject on that
  // teacher's portal. Falls back to the old name-only list on a backend
  // that does not send `staff_teachers` yet, so the picker is never
  // empty; those picks simply carry no id and register without
  // assigning, which the server reports.
  const teachers = (lookups.staff_teachers && lookups.staff_teachers.length)
    ? lookups.staff_teachers
    : (lookups.teachers || EMPTY_LIST);
  const statuses = lookups.subject_statuses || EMPTY_LIST;

  const filledCount = form.subjects.filter(r => r.sub && r.sub.trim()).length;
  const periodReady = !!(activePeriod?.term && activePeriod?.acc);

  // ── Duplicate-subject detection ─────────────────────────────
  //
  // A subject can be registered ONCE per (class · exam type · term ·
  // academic-year). Two flavours of conflict to guard against:
  //
  //   in-form     → the same subject is typed in two rows of THIS
  //                 dialog (e.g. English on row 1 and row 5)
  //   registered  → the subject already exists in ex_re for the same
  //                 (cla, state, term, acc_yer) bucket on the server
  //
  // We fetch the existing-subjects list whenever the class or exam
  // type changes, and surface both kinds of conflict inline on the
  // row + as a blocking error on submit.
  const [existingSet, setExistingSet] = useState(new Set());
  const [localErr, setLocalErr]       = useState('');

  // Sittings that already exist for this class + exam type, and what a
  // new one would be numbered.
  const [sittings, setSittings]             = useState(EMPTY_LIST);
  const [nextSitting, setNextSitting]       = useState(1);
  const [sittingsLoading, setSittingsLoading] = useState(false);

  const repeatable = (lookups.repeatable_exam_states || EMPTY_LIST).some(
    s => String(s).toLowerCase() === String(form.state || '').trim().toLowerCase());

  // What this registration will be called — "Mock 2", or the plain exam
  // name when it only ever has one sitting. Used in the header chip and
  // in the duplicate message, so both name the same thing.
  const sittingText = !repeatable
    ? (form.state || '—')
    : String(form.exam_num ?? 'new') === 'new'
      ? `${form.state} ${nextSitting}`
      : `${form.state} ${form.exam_num}`;

  useEffect(() => {
    // Reset the error whenever the dialog (re)opens so a stale
    // message from the last attempt doesn't linger.
    if (open) setLocalErr('');
  }, [open]);

  useEffect(() => {
    if (!open || !form.cla || !form.state || !periodReady || !repeatable) {
      setSittings(EMPTY_LIST); setNextSitting(1);
      return;
    }
    let cancelled = false;
    setSittingsLoading(true);
    API.get('/examination/subjects/sittings', {
      params: {
        cla: form.cla, state: form.state,
        term: activePeriod.term, acc_yer: activePeriod.acc,
      },
    }).then(r => {
      if (cancelled) return;
      setSittings(Array.isArray(r.data?.sittings) ? r.data.sittings : EMPTY_LIST);
      setNextSitting(Number(r.data?.next) || 1);
    }).catch(() => {
      // An older backend without this route: fall back to "new", which
      // the server still numbers correctly on its own.
      if (!cancelled) { setSittings(EMPTY_LIST); setNextSitting(1); }
    }).finally(() => { if (!cancelled) setSittingsLoading(false); });
    return () => { cancelled = true; };
  }, [open, form.cla, form.state, activePeriod, periodReady, repeatable]);

  // The subjects already taken — judged WITHIN the chosen sitting.
  // The same subject belongs in Mock 1 and again in Mock 2, so checking
  // across the whole exam type would flag every subject of every mock
  // after the first as a duplicate.
  useEffect(() => {
    if (!open || !form.cla || !form.state || !periodReady) {
      setExistingSet(new Set());
      return;
    }
    // A brand-new sitting starts empty, so nothing can clash in it.
    if (repeatable && String(form.exam_num ?? 'new') === 'new') {
      setExistingSet(new Set());
      return;
    }
    let cancelled = false;
    const params = {
      cla:     form.cla,
      state:   form.state,
      term:    activePeriod.term,
      acc_yer: activePeriod.acc,
    };
    if (repeatable && form.exam_num) params.exam_num = form.exam_num;
    API.get('/examination/subjects', { params }).then(r => {
      if (cancelled) return;
      const next = new Set(
        (r.data || []).map(x => String(x.sub || '').trim().toLowerCase()).filter(Boolean)
      );
      setExistingSet(next);
    }).catch(() => { if (!cancelled) setExistingSet(new Set()); });
    return () => { cancelled = true; };
  }, [open, form.cla, form.state, form.exam_num, repeatable, activePeriod, periodReady]);

  // Count how many rows hold each (lower-cased) subject name. >1 is
  // an in-form duplicate.
  const inFormCount = useMemo(() => {
    const m = new Map();
    for (const r of form.subjects) {
      const s = String(r.sub || '').trim().toLowerCase();
      if (!s) continue;
      m.set(s, (m.get(s) || 0) + 1);
    }
    return m;
  }, [form.subjects]);

  // Per-row conflict status used by SubjectRow to colour itself and
  // by the submit guard to refuse a save that would corrupt the
  // unique (cla, state, term, sub) constraint.
  const conflictFor = useCallback((sub) => {
    const s = String(sub || '').trim().toLowerCase();
    if (!s) return null;
    if ((inFormCount.get(s) || 0) > 1) return 'in-form';
    if (existingSet.has(s))            return 'registered';
    return null;
  }, [inFormCount, existingSet]);

  const hasAnyConflict = useMemo(
    () => form.subjects.some(r => conflictFor(r.sub) !== null),
    [form.subjects, conflictFor]
  );

  // Guarded save — runs validation first, only calls the parent's
  // onSave when nothing in the form would violate the per-bucket
  // uniqueness rule.
  const handleGuardedSave = () => {
    setLocalErr('');
    const dupInForm = new Set();
    const dupRegistered = new Set();
    for (const r of form.subjects) {
      const orig = String(r.sub || '').trim();
      const s = orig.toLowerCase();
      if (!s) continue;
      if ((inFormCount.get(s) || 0) > 1) dupInForm.add(orig);
      if (existingSet.has(s))            dupRegistered.add(orig);
    }
    if (dupInForm.size > 0) {
      setLocalErr(
        `The same subject is listed more than once: ${[...dupInForm].join(', ')}. `
        + `Remove the duplicate row(s) — a subject can only be registered once for this class + exam type.`
      );
      return;
    }
    if (dupRegistered.size > 0) {
      setLocalErr(
        `Already registered for ${form.cla} · ${sittingText}: ${[...dupRegistered].join(', ')}. `
        + (repeatable
            ? `Remove these rows, or choose "+ New sitting" to register them as a fresh ${form.state}.`
            : `Remove these rows, edit the existing entry, or pick a different exam type.`)
      );
      return;
    }
    onSave();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      {/* Gradient hero header */}
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 2.2,
        display: 'flex', alignItems: 'center', gap: 1.6,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 46, height: 46 }}>
          <SubjectIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            Register Subjects
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.92 }}>
            {periodReady
              ? `Active period · ${(activePeriod.term || '').replace('_', ' ')} · ${activePeriod.acc}`
              : 'Set the active academic period in Examination Settings first.'}
          </Typography>
        </Box>
        <Chip label={`${filledCount} ready`}
          sx={{
            fontWeight: 800, color: 'primary.main', bgcolor: '#fff',
            height: 28, '& .MuiChip-label': { px: 1.4 },
          }} />
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}
        {localErr && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1.5 }}
            onClose={() => setLocalErr('')}>
            {localErr}
          </Alert>
        )}

        {!periodReady && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No active academic period is set. Open <b>Examination Settings</b> and choose
            the current term &amp; year first — every registration must live inside it.
          </Alert>
        )}

        {/* Set details — class + exam type only */}
        <SectionCard
          title="Set details"
          subtitle="Class + exam type for this registration"
          icon={<ClassIcon fontSize="small" />}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Class" value={form.cla}
                onChange={e => setForm(f => ({ ...f, cla: e.target.value }))}>
                {lookups.classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Exam Type" value={form.state}
                onChange={e => setForm(f => ({
                  ...f, state: e.target.value,
                  // Changing the exam type invalidates the sitting: Mock 2
                  // means nothing once this is a Mid-Term Test.
                  exam_num: 'new',
                }))}>
                {lookups.exam_states.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>

            {/* ── Which sitting? ────────────────────────────────
                A class may sit five or six mocks in one term, so the
                registration has to say WHICH. Choosing an existing
                sitting adds these subjects to it; "New" starts the next
                one. Getting this wrong is the difference between adding
                a forgotten subject to Mock 2 and silently opening a
                Mock 3 — so it is shown, not inferred. */}
            {repeatable && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small"
                  label="Sitting"
                  value={form.exam_num ?? 'new'}
                  onChange={e => setForm(f => ({ ...f, exam_num: e.target.value }))}
                  helperText={
                    sittingsLoading
                      ? 'Checking which sittings already exist…'
                      : String(form.exam_num ?? 'new') === 'new'
                        ? `Starts ${form.state} ${nextSitting} — a brand-new sitting with its own subjects and marks.`
                        : `Adds these subjects to the existing ${form.state} ${form.exam_num}.`
                  }>
                  {sittings.map(s => (
                    <MenuItem key={s.exam_num} value={String(s.exam_num)}>
                      {`${form.state} ${s.exam_num}`}
                      <Typography component="span" variant="caption"
                        sx={{ color: 'text.secondary', ml: 1 }}>
                        {`· ${s.n_subjects} subject${s.n_subjects === 1 ? '' : 's'} already`}
                      </Typography>
                    </MenuItem>
                  ))}
                  <MenuItem value="new">
                    <b>{`+ New sitting (${form.state} ${nextSitting})`}</b>
                  </MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        </SectionCard>

        {/* Subjects */}
        <SectionCard
          title="Subjects"
          subtitle={`${filledCount} of ${form.subjects.length} row${form.subjects.length === 1 ? '' : 's'} filled · empty rows are ignored`}
          icon={<SubjectIcon fontSize="small" />}
          right={
            <Stack direction="row" spacing={1} alignItems="center">
              <Autocomplete freeSolo size="small"
                sx={{ minWidth: 220 }}
                options={teachers}
                value={form.applyTeacher || ''}
                onChange={(_, v) => applyTeacherToAll(v)}
                blurOnSelect
                clearOnBlur={false}
                selectOnFocus
                handleHomeEndKeys
                getOptionLabel={teacherLabel}
                filterOptions={teacherFilter}
                isOptionEqualToValue={(o, v) =>
                  teacherLabel(o).toLowerCase() === teacherLabel(v).toLowerCase()}
                renderOption={(props, o) => (
                  <li {...props} key={o.id ?? o}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{teacherLabel(o)}</Typography>
                      {o?.email && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {o.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Apply teacher to all"
                    placeholder="Optional" />
                )} />
              <Button size="small" variant="outlined" startIcon={<MoreRowsIcon />}
                onClick={addRow}
                sx={{ textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap' }}>
                Add row
              </Button>
            </Stack>
          }>
          <Stack spacing={1}>
            {form.subjects.map((r, i) => (
              <SubjectRow key={r.id} index={i} row={r}
                updateRow={updateRowById}
                onRemove={form.subjects.length > 1 ? removeRowById : null}
                subjectOptions={subjectOptions}
                catalogBySub={catalogBySub}
                statuses={statuses}
                teachers={teachers}
                conflict={conflictFor(r.sub)} />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.2 }}>
            Pick from the dropdown to use a subject the system already knows, or type a new one.
            Use <b>Apply teacher to all</b> when one teacher handles every subject — per-row teachers override it.
          </Typography>
        </SectionCard>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.6, bgcolor: 'background.paper',
        borderTop: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
          <Chip size="small" icon={<SubjectIcon sx={{ fontSize: 14 }} />}
            label={`${filledCount} subject${filledCount === 1 ? '' : 's'}`}
            color={filledCount > 0 ? 'success' : 'default'}
            variant={filledCount > 0 ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }} />
          {form.cla && (
            <Chip size="small" variant="outlined" label={`Class · ${form.cla}`}
              sx={{ fontWeight: 700 }} />
          )}
          {form.state && (
            <Chip size="small" variant="outlined" label={`Exam · ${sittingText}`}
              color={repeatable ? 'secondary' : 'default'}
              sx={{ fontWeight: 700 }} />
          )}
        </Stack>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleGuardedSave}
          disabled={saving || filledCount === 0 || !periodReady || hasAnyConflict}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          sx={{ textTransform: 'none', fontWeight: 800, minWidth: 180,
            boxShadow: '0 4px 14px rgba(25,118,210,0.25)' }}>
          {saving ? 'Registering…'
            : hasAnyConflict ? 'Fix duplicates first'
            : `Register ${filledCount || 'all'} subject${filledCount === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Section card used inside the bulk dialog — keeps the dialog content
// visually organized into distinct chunks with a small header and
// optional right-side action.
function SectionCard({ title, subtitle, icon, right, children }) {
  return (
    <Box sx={{ mb: 2, bgcolor: '#fff', border: 1, borderColor: 'divider',
      borderRadius: 2, overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)' }}>
      <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: 1, borderColor: 'divider',
        bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }}>
        {icon && (
          <Avatar sx={{ width: 26, height: 26,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
            color: 'primary.main' }}>
            {icon}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        {right}
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

// One subject row inside the bulk dialog. Three fields:
//   Subject — Autocomplete over the catalog of subject names (sub).
//   Category — Core / Elective / Mandatory / Optional.
//   Teacher — Autocomplete over the staff roster. Stored in row.sub_t.
// Picking a known subject also pre-fills the Teacher field (only when
// empty), using the catalog's most-frequently-paired teacher.
//
// Perf notes — the previous build had perceptible lag between picking
// an option in the popper and the value showing up in the input. Two
// changes fix that:
//
//   1. `autoSelect` removed. With autoSelect on, MUI defers the commit
//      to the blur event after the popper closes — that's the delay
//      the user was feeling. Without it, an in-popper click commits
//      synchronously on mousedown.
//   2. `blurOnSelect` added. The input loses focus immediately on a
//      selection, so the popper closes faster instead of waiting for
//      MUI's internal animation to finish.
//   3. `filterOptions` caps the rendered list at MAX_OPTIONS_SHOWN.
//      With 50+ subjects / teachers in the school, rendering every
//      <li> on every keystroke is the slowest part — the cap means
//      the popper only ever renders up to that many DOM nodes.
const MAX_OPTIONS_SHOWN = 40;
const subjectFilter = (opts, { inputValue }) => {
  const q = String(inputValue || '').trim().toLowerCase();
  const list = q
    ? opts.filter(o => String(o).toLowerCase().includes(q))
    : opts;
  return list.slice(0, MAX_OPTIONS_SHOWN);
};
// Same cap for the staff picker. Its options are objects, so matching
// reads the name and the email — typing part of either finds the person.
const teacherFilter = (opts, { inputValue }) => {
  const q = String(inputValue || '').trim().toLowerCase();
  const list = q
    ? opts.filter((o) => {
        const name  = (typeof o === 'string' ? o : o?.name || '').toLowerCase();
        const email = (typeof o === 'string' ? '' : o?.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      })
    : opts;
  return list.slice(0, MAX_OPTIONS_SHOWN);
};

// Hoisted out of the component so the same sx ref is reused on every
// render — no inline-object churn for the row wrapper.
const ROW_SX = {
  display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
  p: 1, borderRadius: 1.5, border: '1px solid',
  borderColor: 'divider',
  bgcolor: '#fafbfc',
  transition: 'border-color 120ms, background-color 120ms',
  '&:hover': { borderColor: 'primary.light', bgcolor: '#fff' },
};

const SubjectRow = memo(function SubjectRow({
  index, row, updateRow, onRemove, subjectOptions, catalogBySub, statuses, teachers,
  conflict, // null | 'in-form' | 'registered'
}) {
  const id = row.id;

  const handleSubject = (_, next) => {
    const name = String(next || '').trim();
    const hit  = catalogBySub.get(name);
    const patch = { sub: name };
    if (hit && hit.top_teacher && !(row.sub_t || '').trim()) {
      patch.sub_t = hit.top_teacher;
    }
    updateRow(id, patch);
  };
  // The picker offers staff ACCOUNTS, so a pick carries the user id as
  // well as the name. That id is what turns the choice into a real
  // assignment on the teacher's portal — a name alone cannot, because
  // nothing links it to a login. Free text is still accepted (the
  // server says so plainly when a typed name matches no account).
  // ── Typed text has to count, not just picked options ────────────
  //
  // On a freeSolo Autocomplete, `onChange` fires only when an option is
  // PICKED or Enter is pressed. So typing "Mathematics" and then
  // clicking the Category box left `row.sub` empty: the word was on
  // screen, but it lived in the input's own display state and had never
  // reached the row. The counter read "0 of 1 row filled" and
  // "Register all subjects" stayed dead with nothing saying why.
  //
  // `autoSelect` would also fix it, and is what this file used to do —
  // but it was removed on purpose (see the note above MAX_OPTIONS_SHOWN)
  // because it defers the commit to blur, which is what made picking
  // from the popper feel slow. Committing on each keystroke instead
  // fixes the count without bringing the lag back, and the button
  // enables as you type rather than when you leave the field.
  //
  // `reason` matters: 'input' is the person typing. 'reset' is MUI
  // echoing a selection back, which onChange has already handled —
  // acting on it too would overwrite the picked value with its label.
  const handleSubjectInput = (_, next, reason) => {
    if (reason !== 'input') return;
    updateRow(id, { sub: next });
  };

  const handleTeacher = (_, next) => {
    if (next && typeof next === 'object') {
      updateRow(id, { sub_t: next.name || '', sub_t_id: next.id || null });
    } else {
      updateRow(id, { sub_t: String(next || '').trim(), sub_t_id: null });
    }
  };

  // A typed teacher is not a member of staff until one is picked, so the
  // id is cleared — otherwise an edited name would still carry the
  // previous person's id.
  const handleTeacherInput = (_, next, reason) => {
    if (reason !== 'input') return;
    updateRow(id, { sub_t: next, sub_t_id: null });
  };
  const handleStatus = (e) => updateRow(id, { status: e.target.value });
  const handleRemove = onRemove ? () => onRemove(id) : undefined;

  // Style the row + Subject field with a strong red tint when the
  // typed subject duplicates another row OR is already registered on
  // the server for this (class, exam type, term, year) bucket.
  const isConflict = !!conflict;
  const conflictLabel = conflict === 'registered'
    ? 'Already registered'
    : 'Duplicate in form';
  const conflictTip = conflict === 'registered'
    ? 'This subject is already registered for the chosen class + exam type. Remove this row or pick a different exam type.'
    : 'The same subject is on another row in this form. Each subject can be registered only once per class + exam type.';

  return (
    <Box sx={{
      ...ROW_SX,
      ...(isConflict ? {
        borderColor: 'error.main',
        bgcolor: (t) => alpha(t.palette.error.main, 0.04),
      } : {}),
    }}>
      <Avatar sx={{
        width: 26, height: 26, fontSize: 12, fontWeight: 800,
        bgcolor: isConflict ? 'error.main' : 'primary.main',
      }}>{index + 1}</Avatar>
      <Autocomplete freeSolo size="small"
        sx={{ flex: 2.4, minWidth: 220 }}
        options={subjectOptions}
        value={row.sub || ''}
        onChange={handleSubject}
        // Typed text counts too — see handleSubjectInput. NOT autoSelect:
        // that is deliberately off here (read the note above
        // MAX_OPTIONS_SHOWN) because it defers the commit to blur and
        // that is what made picking from the popper feel laggy.
        onInputChange={handleSubjectInput}
        blurOnSelect
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        filterOptions={subjectFilter}
        renderInput={(params) => (
          <TextField {...params}
            label="Subject"
            placeholder="Pick or type a subject (e.g. Mathematics)"
            error={isConflict}
            helperText={isConflict ? conflictTip : ''} />
        )} />
      <TextField select size="small" label="Category"
        sx={{ minWidth: 150 }}
        value={row.status}
        onChange={handleStatus}>
        {statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
      </TextField>
      <Autocomplete freeSolo size="small"
        sx={{ flex: 1.4, minWidth: 180 }}
        options={teachers}
        value={row.sub_t || ''}
        onChange={handleTeacher}
        // Same as the Subject field: a typed name that is not on the
        // staff list is kept as it is typed, without autoSelect.
        onInputChange={handleTeacherInput}
        blurOnSelect
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        getOptionLabel={teacherLabel}
        filterOptions={teacherFilter}
        isOptionEqualToValue={(o, v) =>
          teacherLabel(o).toLowerCase() === teacherLabel(v).toLowerCase()}
        renderOption={(props, o) => (
          <li {...props} key={o.id ?? o}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{teacherLabel(o)}</Typography>
              {o?.email && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {o.email}
                </Typography>
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Teacher" placeholder="Pick from staff"
            helperText={row.sub_t && !row.sub_t_id ? 'No staff account' : ' '}
            FormHelperTextProps={{ sx: { m: 0, fontSize: 10, color: 'warning.main' } }} />
        )} />
      {isConflict && (
        <Tooltip title={conflictTip}>
          <Chip size="small" color="error" variant="filled"
            icon={conflict === 'registered'
              ? <WarningIcon sx={{ fontSize: 14 }} />
              : <DupInFormIcon sx={{ fontSize: 14 }} />}
            label={conflictLabel}
            sx={{ height: 22, fontWeight: 700 }} />
        </Tooltip>
      )}
      <Tooltip title={handleRemove ? 'Remove this row' : 'At least one row required'}>
        <span>
          <IconButton size="small" color="error" onClick={handleRemove}
            disabled={!handleRemove}>
            <RemoveRowIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}, (prev, next) =>
  // Skip re-render unless this row's own data, its position label, or
  // one of the shared props actually changed identity. All shared
  // props are memoized in the parent so identity equality is reliable.
  prev.row === next.row &&
  prev.index === next.index &&
  prev.conflict === next.conflict &&
  prev.updateRow === next.updateRow &&
  prev.onRemove === next.onRemove &&
  prev.subjectOptions === next.subjectOptions &&
  prev.catalogBySub === next.catalogBySub &&
  prev.statuses === next.statuses &&
  prev.teachers === next.teachers,
);

// ── Single-row edit dialog (used by the pencil icon on each row) ──
function EditSingleDialog({ open, onClose, form, setForm, saving, err, onSave, lookups, catalog }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.secondary.main} 0%, ${t.palette.primary.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}>
          <EditIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            Edit subject
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.92 }}>
            {form.cla || '—'} · {(form.term || '').replace('_', ' ') || '—'} · {form.state || '—'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}
        <Grid container spacing={1.2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Class" value={form.cla}
              onChange={e => setForm(f => ({ ...f, cla: e.target.value }))}>
              {lookups.classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Term" value={form.term}
              onChange={e => setForm(f => ({ ...f, term: e.target.value }))}>
              {TERM_CHOICES.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Academic Year" value={form.acc_yer}
              onChange={e => setForm(f => ({ ...f, acc_yer: e.target.value }))}>
              {lookups.acc_years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Exam Type" value={form.state}
              onChange={e => setForm(f => ({ ...f, state: e.target.value }))}>
              {lookups.exam_states.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete freeSolo autoSelect size="small"
              options={(() => {
                const m = new Map();
                for (const c of catalog) {
                  const n = (c.sub || '').trim();
                  if (n && !m.has(n)) m.set(n, true);
                }
                return [...m.keys()].sort((a, b) => a.localeCompare(b));
              })()}
              value={form.sub || ''}
              onChange={(_, v) => {
                const name = String(v || '').trim();
                setForm(f => ({ ...f, sub: name }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Subject" placeholder="Pick or type a subject" fullWidth />
              )} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Category" value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              {lookups.subject_statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete freeSolo autoSelect size="small"
              options={(lookups.staff_teachers && lookups.staff_teachers.length)
                ? lookups.staff_teachers
                : (lookups.teachers || EMPTY_LIST)}
              value={form.sub_t || ''}
              getOptionLabel={teacherLabel}
              filterOptions={teacherFilter}
              isOptionEqualToValue={(o, v) =>
                teacherLabel(o).toLowerCase() === teacherLabel(v).toLowerCase()}
              onChange={(_, v) => setForm(f => ({ ...f, sub_t: teacherLabel(v) }))}
              renderInput={(params) => <TextField {...params} label="Teacher" placeholder="Pick from staff" />} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.6, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
          sx={{ textTransform: 'none', fontWeight: 800, minWidth: 160,
            boxShadow: '0 4px 14px rgba(25,118,210,0.25)' }}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
